import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import { Modal } from "@mapmycustomers/ui";

import SelectUsersTree from "@app/component/input/SelectUserTree";
import configService from "@app/config/ConfigService";
import authService from "@app/store/auth/AuthService";
import { isCurrentUserOwner } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import { getLegacyAppUrl } from "@app/util/appUrl";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { userDisplayName } from "@app/util/formatters";

import styles from "./SharingModal.module.scss";

interface Props {
  count?: number;
  creatorUserId?: User["id"];
  isGroupSharing?: boolean;
  isOwner: boolean;
  loading?: boolean;
  onChange?: (userIds: User["id"][]) => void;
  onHide: () => void;
  userIds: User["id"][];
}

const SharingModal: React.FC<Props> = ({
  count = 1,
  creatorUserId,
  isGroupSharing,
  isOwner,
  loading,
  onChange,
  onHide,
  userIds,
}) => {
  const analytics = useExtendedAnalytics("Sharing", useAnalytics());
  useEffect(() => {
    analytics.initiated();
  }, [analytics]);

  const intl = useIntl();

  const [innerUserIds, setInnerUserIds] = useState([
    ...(creatorUserId ? [creatorUserId] : []),
    ...userIds,
  ]);
  useEffect(() => {
    setInnerUserIds([...(creatorUserId ? [creatorUserId] : []), ...userIds]);
  }, [creatorUserId, userIds]);

  const handleSave = useCallback(() => {
    onChange?.(innerUserIds);
    onHide();
    analytics.completed("");
  }, [analytics, onChange, onHide, innerUserIds]);

  const handleClose = useCallback(() => {
    onHide();
    analytics.cancelled("");
  }, [analytics, onHide]);

  const isDisabled = useCallback(({ id }: User) => id === creatorUserId, [creatorUserId]);

  const tooltipGetter = useCallback(
    (user: User) => {
      return user.id === creatorUserId
        ? intl.formatMessage(
            {
              id: "map.sidebar.sharingDrawer.users.tree.creator",
              defaultMessage:
                "{isGroupSharing, select, true {{count, select, 1 {Group} other {Groups}}} other {{count, select, 1 {Territory} other {Territories}}}} must be shared with its creator",
              description: "Tooltip for the creator checkbox in SelectUserTree in the Share drawer",
            },
            { count, isGroupSharing }
          )
        : userDisplayName(user);
    },
    [count, intl, creatorUserId, isGroupSharing]
  );

  const token = authService.getToken();
  if (!token) {
    return null;
  }

  return (
    <Modal
      cancelButtonProps={{ hidden: true }}
      okButtonProps={{
        disabled: loading,
        loading: loading,
      }}
      okText={intl.formatMessage(
        {
          id: "map.sidebar.sharePicker.modal.ok",
          defaultMessage:
            "Share {isGroupSharing, select, true {{count, select, 1 {Group} other {Groups ({count})}}} other {{count, select, 1 {Territory} other {Territories}}}}",
          description: "Ok button in Share modal",
        },
        { count, isGroupSharing }
      )}
      onCancel={handleClose}
      onOk={handleSave}
      open
      title={intl.formatMessage(
        {
          id: "map.sidebar.sharePicker.modal.title",
          defaultMessage:
            "Share {isGroupSharing, select, true {{count, select, 1 {Group} other {Groups}}} other {{count, select, 1 {Territory} other {Territories}}}}",
          description: "Title of the Share modal",
        },
        { count, isGroupSharing }
      )}
    >
      <div>
        {intl.formatMessage(
          {
            id: "map.sidebar.sharePicker.modal.info",
            defaultMessage:
              "Any users selected below will have access to <b>view the {isGroupSharing, select, true {{count, select, 1 {group} other {groups}}} other {{count, select, 1 {territory} other {territories}}}} and any records within it</b>.{isOwner, select, true { To modify whether users can edit those records, <link>update their permissions</link>.} other {}}",
            description: "Info in the Share modal",
          },
          {
            b: (texts: string[]) => <b>{texts}</b>,
            count,
            isGroupSharing,
            isOwner,
            link: (texts: string[]) => (
              <a
                href={getLegacyAppUrl(configService.getBaseOldAppUrl(), token, "/dashboard/teams")}
                rel="noopener noreferrer"
                target="_blank"
              >
                {texts}
              </a>
            ),
          }
        )}
      </div>
      <div className={styles.form}>
        <SelectUsersTree
          isDisabled={isDisabled}
          onChange={setInnerUserIds}
          tooltipGetter={tooltipGetter}
          value={innerUserIds}
        />
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOwner: isCurrentUserOwner(state),
});

export default connect(mapStateToProps)(SharingModal);
